import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { FontScaleContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import {
    getProfileData,
    upsertOwnProfileData,
    upsertOwnStatusWithHistory,
    updateCurrentStatusThumbnail,
    deleteOwnProfileData,
    getFriendsStatuses,
    getCommunityStatuses,
    getStatusHistoryItems,
    deleteStatusHistoryItemRow,
    updateStatusHistoryItemRow,
    searchStatusMusic,
    searchStatusMovies,
    searchStatusGames,
    searchStatusBooks,
    searchStatusPodcasts,
} from '../../services/api';
import HabboImage from '../HabboImage';
import { toast } from 'react-toastify';

const STATUS_CATEGORIES = [
    {
        mood: 'Enthusiastic',
        options: [
            'Loving', 'Obsessed with', 'Hooked on', "Can't get enough of",
            'Really into', 'Totally into', 'Deep into', 'Fully immersed in',
            'Blown away by', 'Captivated by', 'Fascinated by', 'Enthralled by',
            'Addicted to', 'Hyped about', 'Raving about', 'Living for',
            'Devouring', 'Consuming', 'Highly recommending', 'Absolutely loving',
            'So invested in', 'Swept up in', 'Mesmerized by', 'In awe of',
            'Passionate about', 'Going crazy for', 'Crushing hard on',
            'Completely captivated by', 'Fixated on', 'Struck by',
            'Adoring', 'Hyped for', 'Thrilled by', 'Energized by',
            'Astonished by', 'Delighted by', 'Enamored with', 'Charmed by',
            'Marveling at', 'Impressed by', 'Super into', 'Going all in on',
            'Completely absorbed by', 'Dazzled by', 'Wild about', 'Fired up about',
            'Stoked about', 'Gushing over', 'Amped for', 'Crazy about'
        ]
    },
    {
        mood: 'Relaxed',
        options: [
            'Enjoying', 'Relaxing with', 'Chilling with', 'Kicking back with',
            'Passing time with', 'Winding down with', 'Killing time with',
            'Taking a break with', 'Vibing with', 'Unwinding with',
            'Coasting through', 'Having fun with', 'Settling in with',
            'Hanging out with', 'Casually enjoying', 'Drifting through',
            'Cruising through', 'Mellowing out to', 'Spending a lazy day with',
            'Zoning out to', 'Taking it easy with', 'Decompressing with',
            'Cooling down with', 'Enjoying a quiet moment with', 'Keeping it lowkey with',
            'Lounging with', 'Spending a cozy evening with', 'Casually consuming',
            'Getting comfortable with', 'Taking my time with', 'Lounging around with',
            'Vegging out to', 'Resting with', 'Soothing myself with', 'Finding peace with',
            'Kicking my feet up with', 'Snoozing to', 'Getting cozy with', 'Staying in with',
            'Whiling away the hours with', 'Basking in', 'Floating through', 'Enjoying the vibe of',
            'Coasting along with', 'Putting my feet up with', 'Destressing with', 'Chilling out to',
            'Taking a breather with', 'Enjoying a lazy afternoon with', 'Settling down with'
        ]
    },
    {
        mood: 'Exploratory',
        options: [
            'Checking out', 'Exploring', 'Giving a go', 'Trying out',
            'Giving a chance', 'Seeing what the fuss is about', 'Finally checking out',
            'Diving into', 'Dipping my toes into', 'Testing out',
            'Experimenting with', 'Investigating', 'Discovering',
            'Uncovering', 'Looking into', 'Sampling',
            'Getting a feel for', 'Learning about', 'Delving into',
            'Navigating through', 'Sizing up', 'Evaluating',
            'Formulating an opinion on', 'Deciding how I feel about', 'Taking a chance on',
            'Experiencing for the first time', 'Peeking at', 'Getting acquainted with',
            'Wrapping my head around', 'Getting started with', 'Unpacking',
            'Figuring out', 'Browsing', 'Scanning through', 'Poking around in',
            'Analyzing', 'Reviewing', 'Assessing', 'Surveying',
            'Scratching the surface of', 'Digging into', 'Getting a taste of', 'Familiarizing myself with',
            'Scoping out', 'Weighing up', 'Taking a tour of', 'Giving a trial run',
            'Making sense of', 'Sifting through', 'Testing the waters of'
        ]
    },
    {
        mood: 'Nostalgic',
        options: [
            'Revisiting', 'Back into', 'Back on', 'Revisiting again',
            'Taking another look at', 'Returning to', 'Feeling nostalgic with',
            'Remembering why I love', 'Re-experiencing', 'Getting reacquainted with',
            'Falling back in love with', 'Reliving', 'Catching up on',
            'Going back to', 'Dusting off', 'Reconnecting with',
            'Missing and returning to', 'Giving another go to', 'Rediscovering',
            'Finding comfort in', 'Rewinding with', 'Sticking to the classics with',
            'Comfort-consuming', 'Firing up the old', 'Having a reunion with',
            'Digging up', 'Pulling out', 'Seeking comfort in',
            'Reliving the magic of', 'Returning home to', 'Thinking back on',
            'Reminiscing with', 'Taking a trip down memory lane with', 'Cherishing', 'Holding onto',
            'Missing the days of', 'Looking back at', 'Getting sentimental over', 'Binging for the hundredth time',
            'Falling into old habits with', 'Resurrecting', 'Bringing back', 'Unearthing',
            'Replaying memories with', 'Reflecting on', 'Turning back the clock with', 'Re-evaluating',
            'Going old school with', 'Appreciating the classics with', 'Experiencing a blast from the past with'
        ]
    },
    {
        mood: 'Critical / Disappointed',
        options: [
            'Annoyed by', 'Frustrated with', 'Struggling through', 'Losing patience with',
            'Rolling my eyes at', 'Battling through', 'Suffering through',
            'Enduring', 'Forcing myself through', 'Disappointed by',
            'Underwhelmed by', 'Not vibing with', 'Giving up on',
            'Critiquing', 'Judging', 'Questioning my choice of',
            'Barely surviving', 'Slogging through', 'Tolerating',
            'Pushing through', 'Gritting my teeth through', 'Losing interest in',
            'Getting bored by', 'Confused by', 'Yelling at',
            'Disagreeing with', 'Sighing heavily at', 'Regretting starting',
            'Finding flaws in', 'Puzzled by', 'Hating',
            'Despising', 'Irritated by', 'Let down by', 'Unimpressed by',
            'Bored to tears by', 'Agonizing over', 'Trudging through', 'Finding tedious',
            'Nitpicking', 'Ranting about', 'Complaining about', 'Shaking my head at',
            'Baffled by', 'Dreading', 'Cringing at', 'Disliking',
            'Barely tolerating', 'Losing my mind over', 'Wasting my time with'
        ]
    },
    {
        mood: 'Humorous / Escapist',
        options: [
            'Hyperfixating on', 'Procrastinating with', 'Avoiding responsibilities with',
            'Emotionally attached to', 'Making this my whole personality', 'Escaping into',
            'Hiding from the world with', 'Ignoring my problems with', 'Losing sleep over',
            'Letting my brain melt to', 'Substituting therapy with', 'Spiraling over',
            'Falling down the rabbit hole of', 'Ruining my life with', 'Dissociating with',
            'Getting overly invested in', 'Pausing reality for', 'Running away from adulting with',
            'Becoming feral over', 'Distracting myself with', 'Refusing to touch grass because of',
            'Forming a parasocial relationship with', 'Canceling plans for', 'Explaining the lore of',
            'Defending my honor over', 'Pretending to understand', 'Entering a new era of',
            'Brainwashing myself with', 'Sacrificing my free time to', 'Selling my soul to',
            'Disappearing into', 'Coping with life via', 'Forgetting what year it is because of', 'Living vicariously through',
            'Dropping everything for', 'Being held hostage by', 'Questioning reality because of', 'Melting into my couch with',
            'Sustaining my life force with', 'Replacing human contact with', 'Entering a trance with', 'Giving up on productivity for',
            'Letting this consume me', 'Finding my will to live in', 'Basing my mood on', 'Letting this dictate my life',
            'Unhealthily obsessed with', 'Burying my head in the sand with', 'Checking out of reality with', 'Willingly being brainwashed by'
        ]
    },
    {
        mood: 'Positive / Approving',
        options: [
            'Approving of', 'Liking', 'Appreciating', 'Respecting',
            'Admiring', 'Supporting', 'Enjoying the vibes of', 'Finding value in',
            'Nodding along to', 'Agreeing with', 'Cheering for', 'Championing',
            'Endorsing', 'Speaking highly of', 'Recommending', 'Acknowledging the greatness of',
            'Finding joy in', 'Being charmed by', 'Smiling at', 'Feeling good about',
            'Taking pleasure in', 'Being pleased by', 'Delighting in', 'Treasuring',
            'Valuing', 'Celebrating', 'Honoring', 'Giving a thumbs up to',
            'Recognizing the brilliance of', 'Getting behind', 'Backing', 'Praising',
            'Commending', 'Applauding', 'Marveling over', 'Savouring',
            'Relishing', 'Being gladdened by', 'Getting a kick out of', 'Feeling uplifted by',
            'Finding satisfaction in', 'Being moved by', 'Feeling touched by', 'Being inspired by',
            'Gaining a new appreciation for', 'Warming up to', 'Finding delightful', 'Enjoying the craft of',
            'Respecting the artistry of', 'Feeling positive about'
        ]
    },
    {
        mood: 'Casual / Indifferent',
        options: [
            'Having on in the background', 'Skimming', 'Half-watching', 'Half-listening to',
            'Multitasking with', 'Having on for ambient noise', 'Passively consuming', 'Glancing at',
            'Paying half-attention to', 'Keeping on just for noise', 'Having on while working', 'Having on while cleaning',
            'Putting on for background noise', 'Not really paying attention to', 'Tuning in and out of', 'Casually observing',
            'Mildly engaging with', 'Half-heartedly checking out', 'Idly consuming', 'Giving minimal effort to',
            'Putting on autojump for', 'Using as white noise', 'Barely processing', 'Distractedly consuming',
            'Letting play in the background', 'Having on while scrolling', 'Half-absorbing', 'Passively absorbing',
            'Not really focusing on', 'Letting wash over me', 'Half-ignoring', 'Mindlessly consuming',
            'Treating as background decoration', 'Not deeply invested in', 'Taking or leaving', 'Feeling indifferent towards',
            'Having on for the sake of it', 'Just letting it play', 'Having on while falling asleep', 'Paying zero attention to',
            'Tuning out', 'Simply existing near', 'Keeping on in another tab', 'Using as a distraction',
            'Briefly acknowledging', 'Flicking through', 'Glancing over', 'Treating as elevator music',
            'Having a casual relationship with', 'Keeping strictly casual with'
        ]
    },
    {
        mood: 'Focused / Intense',
        options: [
            'Studying', 'Analyzing', 'Grinding', 'Deep diving into',
            'Focusing on', 'Picking apart', 'Deconstructing', 'Examining',
            'Scrutinizing', 'Taking notes on', 'Memorizing', 'Mastering',
            'Deciphering', 'Paying close attention to', 'Getting into the weeds of', 'Dissecting',
            'Breaking down', 'Closely observing', 'Intensely focusing on', 'Putting my brain to work on',
            'Going hard on', 'Locking into', 'Zeroing in on', 'Sweating over',
            'Putting the hours into', 'Grinding through', 'Speedrunning', 'Powering through',
            'Bingeing intensely', 'Taking seriously', 'Applying myself to', 'Figuring out the mechanics of',
            'Learning the ins and outs of', 'Immersing myself deeply in', 'Treating like a full-time job', 'Poring over',
            'Meticulously reviewing', 'Hunting for details in', 'Mapping out', 'Theorizing about',
            'Connecting the dots in', 'Researching', 'Going over with a fine-tooth comb', 'Searching for easter eggs in',
            'Analyzing the lore of', 'Looking for hidden meanings in', 'Deeply engaged with', 'Getting technical with',
            'Obsessively tracking', 'Systematically consuming'
        ]
    },
    {
        mood: 'Social / Shared',
        options: [
            'Enjoying with friends', 'Discussing', 'Sharing', 'Watching with',
            'Listening with', 'Playing with', 'Experiencing with', 'Binging with',
            'Bonding over', 'Talking about', 'Debating', 'Reviewing with others',
            'Recommending to friends', 'Forcing my friends to try', 'Experiencing as a group', 'Hosting a watch party for',
            'Co-oping', 'Tag-teaming', 'Going through with my partner', 'Introducing to someone else',
            'Arguing with friends about', 'Getting the group chat into', 'Live-reacting to', 'Screaming with friends over',
            'Sharing the pain of', 'Theorizing with friends about', 'Hyping up with others', 'Enjoying the community around',
            'Participating in the fandom of', 'Joining the conversation about', 'Connecting with others over', 'Making memories with',
            'Laughing with friends at', 'Crying with friends over', 'Ranting to friends about', 'Geeking out with others over',
            'Comparing notes on', 'Reading along with', 'Playing along with', 'Singing along with',
            'Following along with', 'Going on a shared journey through', 'Tackling together with', 'Taking turns with',
            'Passing the controller for', 'Experiencing the hype of', 'Being social with', 'Joining the book club for',
            'Participating in the discussion for', 'Enjoying the zeitgeist of'
        ]
    }
];

const MEDIA_TYPES = [
    { key: 'music', label: 'Music', icon: '♪' },
    { key: 'movie', label: 'Movie', icon: '▶' },
    { key: 'tv', label: 'TV Show', icon: '□' },
    { key: 'game', label: 'Game', icon: '◈' },
    { key: 'book', label: 'Book', icon: '▣' },
    { key: 'podcast', label: 'Podcast', icon: '◉' },
];

const FRIENDS_BATCH = 15;
const HISTORY_BATCH = 15;
const HISTORY_MAX = 100;
const COMMUNITY_LIMIT = 48;
const SPHERE_RADIUS = 210;
const STATUS_COOLDOWN_MS = 30 * 60 * 1000;
const COMMUNITY_AUTO_REFRESH_MS = 15 * 60 * 1000;
const COMMUNITY_PLACEHOLDER_COUNT = 80;


const _thumbCache = new Map();

const fetchImageAsWebP = async (url, maxSize = 190) => {
    if (!url || url.startsWith('data:')) return url;
    const key = `${url}@${maxSize}`;
    if (_thumbCache.has(key)) return _thumbCache.get(key);
    try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
        });
        const nw = img.naturalWidth || maxSize;
        const nh = img.naturalHeight || maxSize;
        const side = Math.min(nw, nh);
        const sx = (nw - side) / 2;
        const sy = (nh - side) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = maxSize;
        canvas.height = maxSize;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, side, side, 0, 0, maxSize, maxSize);
        const dataUrl = canvas.toDataURL('image/webp', 0.82);
        const result = dataUrl.startsWith('data:image/webp') ? dataUrl : canvas.toDataURL('image/jpeg', 0.82);
        _thumbCache.set(key, result);
        return result;
    } catch {
        _thumbCache.set(key, url);
        return url;
    }
};

const hexToRgb = (hex) => {
    const h = String(hex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return { r: 255, g: 255, b: 255 };
    return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};

const rgbToHex = ({ r, g, b }) => {
    const to = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
};

const mixToWhite = (hex, t) => {
    const c = hexToRgb(hex);
    return rgbToHex({
        r: c.r + (255 - c.r) * t,
        g: c.g + (255 - c.g) * t,
        b: c.b + (255 - c.b) * t,
    });
};

const srgbToLinear = (v) => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
};

const luminance = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
};

const contrastRatio = (bg, txt) => {
    const L1 = luminance(bg);
    const L2 = luminance(txt);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
};

const ensureReadable = (bgHex, minRatio = 4.5) => {
    let bg = String(bgHex || '#ffffff');
    for (let i = 0; i < 20; i += 1) {
        if (contrastRatio(bg, '#222222') >= minRatio) return bg;
        bg = mixToWhite(bg, 0.08);
    }
    return bg;
};

const buildStatusPalette = (headerHex) => {
    const raw = String(headerHex || '').trim();
    const valid = /^#[0-9A-Fa-f]{6}$/.test(raw);
    return {
        containerBg: valid ? ensureReadable(mixToWhite(raw, 0.82)) : '#e8e4e0',
        cardBg: valid ? ensureReadable(mixToWhite(raw, 0.93)) : '#ffffff',
    };
};

const timeAgo = (timestamp) => {
    if (!timestamp) return null;
    const secs = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
};

const cardKey = (card, i) => `${card.habboName || 'unknown'}-${card.timestamp || card.updatedAt || i}`;

const normaliseIncomingStatus = (item) => {
    if (!item) return null;

    const raw = item.status && typeof item.status === 'object' ? item.status : item;
    if (!raw || typeof raw !== 'object') return null;

    return {
        ...raw,
        habboName: item.habboName || raw.habboName || 'Unknown',
        updatedAt: item.updatedAt || raw.updatedAt || null,
        timestamp: raw.timestamp || item.updatedAt || raw.updatedAt || null,
        isOwn: !!item.isOwn,
        noStatus: !!raw.noStatus,
    };
};

const buildCommunityPlaceholders = () =>
    Array.from({ length: COMMUNITY_PLACEHOLDER_COUNT }, (_, i) => ({
        id: `placeholder-${i}`,
        placeholder: true,
        habboName: `User ${i + 1}`,
        title: `Community status ${i + 1}`,
        subtitle: 'Active now',
        phrase: 'Currently enjoying',
        badges: [],
        thumbnail: null,
        timestamp: null,
        media_type: 'music',
        isOwn: false,
    }));

const CARD_HEIGHT = 350;

const StatusCard = ({ card, isOwnCard, onDelete, palette }) => {
    const { phrase, title, subtitle, badges = [], thumbnail, habboName, media_type, noStatus, timestamp } = card;
    const mediaIcon = MEDIA_TYPES.find((m) => m.key === media_type)?.icon || '?';
    const navigate = useNavigate();
    const timeStr = timeAgo(timestamp);

    return (
        <div
            className="relative flex flex-col shrink-0 border border-black overflow-hidden select-none w-[190px] h-[350px] shadow-[2px_2px_0_rgba(0,0,0,0.08),_4px_4px_0_rgba(0,0,0,0.04)]"
            style={{ background: palette.cardBg }}
        >
            <div className="relative w-full shrink-0 bg-black/8 overflow-hidden h-[190px]">
                {noStatus ? (
                    <div className="w-full h-full flex items-center justify-center">
                        <span className="text-5xl text-black/10 select-none">?</span>
                    </div>
                ) : thumbnail ? (
                    <img src={thumbnail} alt={title} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <span className="text-6xl text-black/10 select-none">{mediaIcon}</span>
                    </div>
                )}

                {isOwnCard && !noStatus && (
                    <button
                        type="button"
                        onClick={onDelete}
                        className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center bg-black/50 hover:bg-red-900/80 text-white transition-colors"
                        title="Remove status"
                    >
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}
            </div>

            <div className="flex flex-col flex-1 px-2.5 pt-2 pb-1 gap-0.5 overflow-hidden">
                {noStatus ? (
                    <div className="flex flex-1 items-center justify-center">
                        <span className="text-sm text-coal/50 italic">No status set</span>
                    </div>
                ) : (
                    <>
                        <div className="shrink-0 text-[11px] text-coal/60 italic leading-snug truncate">{phrase}</div>
                        <div className="shrink-0 font-['Jersey_10'] text-[20px] leading-tight text-coal line-clamp-2" title={title}>{title}</div>
                        {subtitle && <div className="shrink-0 text-[11px] text-coal/70 leading-snug truncate" title={subtitle}>{subtitle}</div>}
                        {badges.length > 0 && (
                            <div className="shrink-0 flex gap-1 flex-wrap mt-0.5 overflow-hidden max-h-[20px]">
                                {badges.slice(0, 2).map((b, i) => (
                                    <span key={i} className="text-[9px] text-coal/60 bg-black/5 px-1 py-px border border-black/10 leading-snug truncate max-w-full">{b}</span>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <div
                className="shrink-0 px-2.5 pb-1.5 border-t border-black/10 flex items-end justify-between gap-1 cursor-pointer hover:bg-black/5 transition-colors"
                onClick={() => navigate(`/members/profile/${encodeURIComponent(habboName)}`)}
            >
                <div className="flex flex-col min-w-0">
                    <span className="text-[12px] text-coal/60 font-bold truncate max-w-[110px]">{habboName}</span>
                    {timeStr && <span className="text-[11px] text-coal/45 leading-tight">{timeStr}</span>}
                </div>
                <div className="overflow-hidden shrink-0 h-[40px] -mb-1.5">
                    <HabboImage
                        habbo={habboName}
                        hotel="COM"
                        size="a"
                        direction={3}
                        head_direction={3}
                        gesture="sml"
                        headonly="true"
                        className="block"
                        style={{ imageRendering: 'pixelated' }}
                    />
                </div>
            </div>
        </div>
    );
};

const SphereDot = React.memo(React.forwardRef(({ position, card, onEnter, onLeave, onClickTile, isClicked }, ref) => {
    const { x, y, z, rotateY, rotateX, randomTiltX, randomTiltY, randomTiltZ, flipped, absY, color, opacity } = position;
    const thumb = card?.thumbnail;

    const pf = isClicked ? 1.28 : 1;
    return (
        <div
            ref={ref}
            style={{
                position: 'absolute',
                width: '50px',
                height: '50px',
                left: '-25px',
                top: '-25px',
                transform: `translate3d(${x * pf}px, ${y * pf}px, ${z * pf}px) rotateY(${90 - rotateY}deg) rotateX(${-rotateX}deg) rotateX(${randomTiltX}deg) rotateY(${randomTiltY}deg) rotateZ(${flipped ? randomTiltZ + 180 : randomTiltZ}deg) scale(${(1 - absY * 0.22).toFixed(3)})`,
                opacity: isClicked ? 1 : opacity,
                willChange: 'transform',
                transition: 'transform 0.38s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease',
            }}
        >
            <div
                className="fsphere-dot w-full h-full bg-[#111] rounded-[4px] cursor-pointer overflow-hidden"
                onMouseEnter={() => onEnter(card)}
                onMouseLeave={onLeave}
                onClick={(e) => onClickTile(card, position.id, e)}
                style={{
                    border: `1px solid ${isClicked ? 'rgba(255,215,0,0.9)' : color}`,
                    '--dot-color': color,
                    ...(isClicked ? {
                        boxShadow: '0 0 18px 5px rgba(255,210,0,0.95), 0 0 45px 18px rgba(255,170,0,0.5), 0 0 80px 30px rgba(255,140,0,0.25)',
                    } : {}),
                }}
            >
                {thumb ? (
                    <img src={thumb} alt={card?.title || card?.habboName}
                        className="w-full h-full object-cover block" />
                ) : (
                    <div className="w-full h-full opacity-70" style={{ background: `linear-gradient(45deg, #222, ${color})` }} />
                )}
            </div>
        </div>
    );
}), (prev, next) =>
    prev.card === next.card &&
    prev.position === next.position &&
    prev.onEnter === next.onEnter &&
    prev.onLeave === next.onLeave &&
    prev.onClickTile === next.onClickTile &&
    prev.isClicked === next.isClicked
);

const SPHERE_STYLE = `
  .fsphere-dot{transition:transform 0.2s ease,box-shadow 0.2s ease}
  .fsphere-dot:hover{transform:scale(1.35);box-shadow:0 0 20px var(--dot-color)}
  @keyframes sphereExit{
    0%  {transform:scale(1) rotate(0deg);opacity:1}
    35% {transform:scale(1.18) rotate(10deg);opacity:0.7}
    100%{transform:scale(0) rotate(25deg);opacity:0}
  }
  @keyframes sphereEnter{
    0%  {transform:scale(0);opacity:0;box-shadow:0 0 0px gold}
    35% {transform:scale(1.25);opacity:1;box-shadow:0 0 28px gold}
    70% {transform:scale(0.95);box-shadow:0 0 14px gold}
    100%{transform:scale(1);opacity:1;box-shadow:0 0 0px gold}
  }
  .tile-exit .fsphere-dot{animation:sphereExit 0.5s ease forwards!important;transition:none!important}
  .tile-enter .fsphere-dot{animation:sphereEnter 0.95s ease forwards!important;transition:none!important}
  @keyframes fsphere-stars-scroll{from{transform:translateX(0)}to{transform:translateX(-236px)}}
  .fsphere-stars{position:absolute;top:0;bottom:0;left:0;width:calc(100% + 236px);background-image:url(/img/brightstars.png);background-repeat:repeat;background-size:236px 448px;image-rendering:pixelated;animation:fsphere-stars-scroll 90s linear infinite;will-change:transform;z-index:-10;pointer-events:none}
  @keyframes fsphere-frank-bob{0%,100%{transform:translateY(calc(-50% + 0px))}50%{transform:translateY(calc(-50% - 10px))}}
`;

const _SPHERE_SLOT_ORDER = (() => {
    let s = 1982734651;
    const rng = () => { s = Math.imul(1664525, s) + 1013904223 | 0; return (s >>> 0) / 4294967296; };
    const arr = Array.from({ length: COMMUNITY_PLACEHOLDER_COUNT }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
})();

const CommunitySphere = ({ cards, palette, loading }) => {
    const sphereRef = useRef(null);
    const rotRef = useRef({ y: Math.random() * 360, x: (Math.random() - 0.5) * 60 });
    const pausedRef = useRef(false);
    const speedRef = useRef(1);
    const isHoveringRef = useRef(false);
    const rafRef = useRef(null);
    const dotRefsRef = useRef([]);

    const selectedRef = useRef(null);
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [hoverCard, setHoverCard] = useState(null);
    const previewCard = hoverCard ?? selectedRef.current?.card ?? null;

    const hasBeenPopulatedRef = useRef(false);

    const tiltsRef = useRef(null);
    if (!tiltsRef.current) {
        tiltsRef.current = Array.from({ length: COMMUNITY_PLACEHOLDER_COUNT }, () => ({
            randomTiltX: (Math.random() - 0.5) * 24,
            randomTiltY: (Math.random() - 0.5) * 24,
            flipped: Math.random() < 0.5,
            randomTiltZ: (Math.random() - 0.5) * 40,
        }));
    }

    const slotMetaRef = useRef(null);
    if (!slotMetaRef.current) {
        slotMetaRef.current = Array.from({ length: COMMUNITY_PLACEHOLDER_COUNT }, () => ({ filledAt: null }));
    }

    const positions = useMemo(() => {
        const n = COMMUNITY_PLACEHOLDER_COUNT;
        const phi = Math.PI * (3 - Math.sqrt(5));
        const tilts = tiltsRef.current;
        const raw = Array.from({ length: n }, (_, i) => {
            const yNorm = 1 - (i / Math.max(n - 1, 1)) * 2;
            const radiusAtY = Math.sqrt(1 - yNorm * yNorm);
            const theta = phi * i;
            const z = Math.sin(theta) * radiusAtY * SPHERE_RADIUS;
            return {
                x: Math.cos(theta) * radiusAtY * SPHERE_RADIUS,
                y: yNorm * SPHERE_RADIUS,
                z,
                absY: Math.abs(yNorm),
                rotateY: (theta * 180) / Math.PI,
                rotateX: (Math.asin(yNorm) * 180) / Math.PI,
                randomTiltX: tilts[i]?.randomTiltX ?? 0,
                randomTiltY: tilts[i]?.randomTiltY ?? 0,
                randomTiltZ: tilts[i]?.randomTiltZ ?? 0,
                flipped: tilts[i]?.flipped ?? false,
                opacity: 0.65 + 0.35 * ((z / SPHERE_RADIUS + 1) / 2),
                color: `hsl(${(i * 360) / n}, 60%, 50%)`,
            };
        });
        raw.sort((a, b) => a.absY - b.absY);
        return raw.map((p, i) => ({ ...p, id: i }));
    }, []);

    const buildSlots = (cardsArr) => {
        const ordered = cardsArr.slice().reverse();
        const placeholders = buildCommunityPlaceholders();

        slotMetaRef.current = Array.from({ length: COMMUNITY_PLACEHOLDER_COUNT }, () => ({ filledAt: null }));

        const slots = Array.from({ length: COMMUNITY_PLACEHOLDER_COUNT }, (_, i) => placeholders[i]);
        ordered.forEach((card, cardIdx) => {
            const slotIdx = _SPHERE_SLOT_ORDER[cardIdx];
            if (slotIdx !== undefined) {
                slots[slotIdx] = card;
                slotMetaRef.current[slotIdx] = { filledAt: cardIdx + 1 };
            }
        });
        return slots;
    };

    const [cardSlots, setCardSlots] = useState(() => buildSlots(cards));

    const cardSlotsRef = useRef(cardSlots);
    useEffect(() => { cardSlotsRef.current = cardSlots; }, [cardSlots]);

    const prevCardsRef = useRef(cards);
    useEffect(() => {
        if (prevCardsRef.current === cards) return;
        prevCardsRef.current = cards;
        const hasReal = cards.some(c => !c.placeholder);
        if (!hasReal) {
            hasBeenPopulatedRef.current = false;
            setCardSlots(buildSlots(cards));
        } else if (!hasBeenPopulatedRef.current) {
            hasBeenPopulatedRef.current = true;
            setCardSlots(buildSlots(cards));
        }
    }, [cards]);

    useEffect(() => {
        const animate = () => {
            const target = pausedRef.current ? 0 : 1;
            speedRef.current += (target - speedRef.current) * 0.055;
            if (speedRef.current > 0.001) {
                rotRef.current.y += 0.12 * speedRef.current;
                rotRef.current.x += 0.018 * speedRef.current;
            }
            if (sphereRef.current) {
                sphereRef.current.style.transform =
                    `rotateY(${rotRef.current.y}deg) rotateX(${rotRef.current.x}deg)`;
            }
            rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafRef.current);
    }, []);

    useEffect(() => {
        const handler = (e) => {
            const newCard = normaliseIncomingStatus(e.detail);
            if (!newCard) return;

            const meta = slotMetaRef.current;
            const freeIndices = meta.reduce((acc, m, i) => {
                if (m.filledAt === null) acc.push(i);
                return acc;
            }, []);
            let targetIdx;
            if (freeIndices.length > 0) {
                targetIdx = freeIndices[Math.floor(Math.random() * freeIndices.length)];
            } else {
                targetIdx = meta.reduce((oldest, m, i) =>
                    m.filledAt < meta[oldest].filledAt ? i : oldest, 0);
            }
            meta[targetIdx] = { filledAt: Date.now() };

            const isPlaceholder = cardSlotsRef.current[targetIdx]?.placeholder === true;
            const targetEl = dotRefsRef.current[targetIdx];

            const doSwap = () => {
                setCardSlots(prev => {
                    const next = [...prev];
                    next[targetIdx] = newCard;
                    return next;
                });
                requestAnimationFrame(() => requestAnimationFrame(() => {
                    const el = dotRefsRef.current[targetIdx];
                    if (el) {
                        el.classList.add('tile-enter');
                        setTimeout(() => el.classList.remove('tile-enter'), 1000);
                    }
                }));
            };

            if (!isPlaceholder && targetEl) {
                targetEl.classList.add('tile-exit');
                setTimeout(() => {
                    targetEl.classList.remove('tile-exit');
                    doSwap();
                }, 500);
            } else {
                doSwap();
            }
        };

        window.addEventListener('frankly:statusPosted', handler);
        return () => window.removeEventListener('frankly:statusPosted', handler);
    }, []);

    const handleEnter = useCallback((card) => {
        if (selectedRef.current) return;
        isHoveringRef.current = true;
        pausedRef.current = true;
        if (!card?.placeholder) setHoverCard(card);
    }, []);

    const handleLeave = useCallback(() => {
        setHoverCard(null);
        if (selectedRef.current) return;
        isHoveringRef.current = false;
        pausedRef.current = false;
    }, []);

    const handleClickTile = useCallback((card, idx, e) => {
        e.stopPropagation();
        if (!card || card.placeholder) return;
        if (selectedRef.current?.idx === idx) {
            selectedRef.current = null;
            setSelectedIdx(null);
            setHoverCard(null);
            pausedRef.current = isHoveringRef.current;
        } else {
            selectedRef.current = { card, idx };
            setSelectedIdx(idx);
            setHoverCard(null);
            pausedRef.current = true;
        }
    }, []);

    const handleSphereClick = useCallback(() => {
        if (!selectedRef.current) return;
        selectedRef.current = null;
        setSelectedIdx(null);
        setHoverCard(null);
        isHoveringRef.current = false;
        pausedRef.current = false;
    }, []);

    return (
        <div
            className="relative w-full h-[620px] bg-[#050505] flex items-center justify-center overflow-hidden"
            style={{ perspective: '1200px' }}
            onClick={handleSphereClick}
        >
            <style>{SPHERE_STYLE}</style>
            <div className="fsphere-stars" />

            <img
                src={`${process.env.PUBLIC_URL}/img/spaceDuck.png`}
                alt=""
                width={31}
                height={31}
                className="absolute z-[-1] pointer-events-none"
                style={{ scale: 2, top: 'calc(50% - 100px)', left: 'calc(50% + 300px)', imageRendering: 'pixelated', animation: 'fsphere-frank-bob 3.5s ease-in-out infinite', willChange: 'transform' }}
            />

            <img
                src={`${process.env.PUBLIC_URL}/img/spaceFrank.png`}
                alt=""
                width={92}
                height={176}
                className="absolute z-[-1] pointer-events-none"
                style={{top: 'calc(50% + 50px)', left: 'calc(50% - 370px)', imageRendering: 'pixelated', animation: 'fsphere-frank-bob1 3.5s ease-in-out infinite', willChange: 'transform' }}
            />

            <img
                src={`${process.env.PUBLIC_URL}/img/proimager/space_bg_1.png`}
                alt=""
                width={650}
                height={500}
                className="absolute z-[-2] pointer-events-none"
                style={{ scale: 2, top: 'calc(50% - 50px)', left: 'calc(50% - 900px)', imageRendering: 'pixelated', animation: 'fsphere-frank-bob1 3.5s ease-in-out infinite', willChange: 'transform' }}
            />

            <div
                ref={sphereRef}
                className="relative w-px h-px"
                style={{ transformStyle: 'preserve-3d', transform: 'rotateY(0deg) rotateX(-10deg)', willChange: 'transform' }}
            >
                {positions.map((pos, i) => (
                    <SphereDot
                        key={pos.id}
                        ref={el => { dotRefsRef.current[i] = el; }}
                        position={pos}
                        card={cardSlots[i]}
                        onEnter={handleEnter}
                        onLeave={handleLeave}
                        onClickTile={handleClickTile}
                        isClicked={selectedIdx === pos.id}
                    />
                ))}
            </div>

            <div className="absolute inset-0 pointer-events-none z-[-5]" style={{ background: 'radial-gradient(ellipse 55% 55% at 50% 50%, transparent 15%, rgba(0,0,0,0.75) 100%)' }} />

            <div className="absolute top-5 left-0 right-0 z-10 flex justify-center font-['Volter'] text-[9px] text-offwhite text-center">
                The FranklySphere is a glimpse into what people enjoy, hate, tolerate and forget about!
            </div>

            {previewCard && (
                <div
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] ${selectedIdx !== null ? '' : 'pointer-events-none'}`}
                    onClick={e => e.stopPropagation()}
                >
                    <StatusCard card={previewCard} isOwnCard={false} onDelete={() => {}} palette={palette} />
                </div>
            )}

            {loading && cardSlots.every(s => s?.placeholder) && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[2] font-['Volter'] text-[9px] px-2 py-1 text-white/65 bg-black/35 border border-white/[0.08]">
                    Loading FranklySphere…
                </div>
            )}
        </div>
    );
};

const StatusCreationModal = ({ onSave, onClose }) => {
    const fontScale = useContext(FontScaleContext);
    const [step, setStep] = useState(1);
    const [phrase, setPhrase] = useState('');
    const [activeMood, setActiveMood] = useState(STATUS_CATEGORIES[0].mood);
    const [mediaType, setMediaType] = useState('');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);

    const abortRef = useRef(null);

    const runSearch = useCallback(async (q, mt) => {
        if (!q.trim() || !mt) return;
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setSearching(true);
        setResults([]);
        const token = abortRef.current;
        try {
            let data;
            if (mt === 'music')                    data = await searchStatusMusic(q);
            else if (mt === 'movie' || mt === 'tv') data = await searchStatusMovies(q);
            else if (mt === 'game')                 data = await searchStatusGames(q);
            else if (mt === 'book')                 data = await searchStatusBooks(q);
            else if (mt === 'podcast')              data = await searchStatusPodcasts(q);
            if (!token.signal.aborted) setResults(data?.results || []);
        } catch (err) {
            if (err.name !== 'AbortError') console.error('[StatusSearch]', err);
        } finally {
            if (!token.signal.aborted) setSearching(false);
        }
    }, []);

    const handleSelect = (item) => {
        setSelected({
            title: item.title,
            subtitle: item.subtitle || null,
            badges: item.badges || [],
            artworkUrl: item.artworkUrl || null,
        });
        setResults([]);
    };

    const handleSave = async () => {
        if (!phrase || !mediaType || !selected) return;
        setSaving(true);
        try {
            await onSave({
                phrase,
                media_type: mediaType,
                title: selected.title,
                subtitle: selected.subtitle,
                badges: selected.badges || [],
                timestamp: new Date().toISOString(),
                thumbnail: selected.artworkUrl || null,
            });
        } catch (err) {
            toast.error('Failed to save status.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ zoom: 1 / fontScale }}>
            <div className="bg-offwhite border-2 border-black w-full max-w-[560px] max-h-[65vh] flex flex-col overflow-hidden shadow-[8px_8px_0_rgba(0,0,0,0.3)]">
                <div className="flex items-center justify-between px-4 py-3 bg-coal text-offwhite shrink-0">
                    <span className="font-['Jersey_10'] text-2xl uppercase tracking-wide">
                        {step === 1 ? 'Pick a phrase' : step === 2 ? 'What type?' : 'Find it'}
                    </span>
                    <button type="button" onClick={onClose} className="text-offwhite/50 hover:text-gold transition-colors">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="flex shrink-0 border-b border-black/10">
                    {[1, 2, 3].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => s < step && setStep(s)}
                            className={`flex-1 py-1.5 text-xs font-bold transition-colors ${step === s ? 'bg-gold text-coal' : s < step ? 'bg-black/5 text-coal/55 hover:bg-black/10 cursor-pointer' : 'bg-black/2 text-coal/20 cursor-default'}`}
                        >
                            {s === 1 ? 'Phrase' : s === 2 ? 'Type' : 'Item'}
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto habbo-scroll-v">
                    {step === 1 && (
                        <div className="flex flex-col min-h-0">
                            <div className="flex overflow-x-auto shrink-0 border-b border-black/10 habbo-scroll-h">
                                {STATUS_CATEGORIES.map((cat) => (
                                    <button
                                        key={cat.mood}
                                        type="button"
                                        onClick={() => setActiveMood(cat.mood)}
                                        className={`shrink-0 px-3 py-2 text-xs font-bold whitespace-nowrap border-r border-black/8 last:border-r-0 transition-colors ${activeMood === cat.mood ? 'bg-coal text-offwhite' : 'text-coal/55 hover:bg-black/5'}`}
                                    >
                                        {cat.mood}
                                    </button>
                                ))}
                            </div>
                            <div className="flex flex-col py-1 overflow-y-auto habbo-scroll-v">
                                {STATUS_CATEGORIES.find((c) => c.mood === activeMood)?.options.map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => { setPhrase(p); setStep(2); }}
                                        className={`w-full text-left px-4 py-2.5 text-sm border-b border-black/6 transition-colors ${phrase === p ? 'bg-gold/20 font-bold text-coal' : 'text-coal hover:bg-gold/8'}`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="grid grid-cols-2 gap-px p-px bg-black/10">
                            {MEDIA_TYPES.map((mt) => (
                                <button
                                    key={mt.key}
                                    type="button"
                                    onClick={() => { setMediaType(mt.key); setSelected(null); setResults([]); setQuery(''); setStep(3); }}
                                    className="flex flex-col items-center justify-center gap-2 py-7 bg-offwhite hover:bg-gold/10 transition-colors"
                                >
                                    <span className="text-4xl leading-none text-coal/50">{mt.icon}</span>
                                    <span className="text-xs font-bold uppercase tracking-wide text-coal/65">{mt.label}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="flex flex-col">
                            <div className="px-4 py-2 bg-black/3 border-b border-black/8 text-xs text-coal/45 italic">
                                "{phrase}" · {MEDIA_TYPES.find((m) => m.key === mediaType)?.label}
                            </div>
                            <form className="px-3 pt-3 pb-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); runSearch(query, mediaType); }}>
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={`Search ${MEDIA_TYPES.find((m) => m.key === mediaType)?.label}…`}
                                    className="flex-1 px-3 py-2 text-sm border border-black/20 bg-white text-coal focus:outline-none focus:border-coal/35"
                                    autoFocus
                                />
                                <button
                                    type="submit"
                                    disabled={searching || !query.trim()}
                                    className="px-3 py-2 text-xs font-bold bg-coal text-offwhite border border-black/40 hover:bg-gold hover:text-coal hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
                                >
                                    {searching ? '…' : 'Search'}
                                </button>
                            </form>

                            {searching && <div className="text-xs text-coal/35 text-center py-2">Searching…</div>}

                            {selected && !results.length && (
                                <div className="mx-3 mb-2 flex items-center gap-3 border border-gold/35 bg-gold/6 px-3 py-2">
                                    {selected.artworkUrl && <img src={selected.artworkUrl} alt="" className="w-12 h-12 object-cover shrink-0 border border-black/10" />}
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold text-coal truncate">{selected.title}</div>
                                        {selected.subtitle && <div className="text-[11px] text-coal/50 truncate">{selected.subtitle}</div>}
                                    </div>
                                </div>
                            )}

                            {results.length > 0 && (
                                <div className="flex flex-col border-t border-black/8 max-h-[280px] overflow-y-auto habbo-scroll-v">
                                    {results.map((item, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => handleSelect(item)}
                                            className="flex items-center gap-3 px-3 py-2.5 text-left border-b border-black/6 last:border-0 hover:bg-gold/10 transition-colors"
                                        >
                                            {item.artworkUrl ? (
                                                <img src={item.artworkUrl} alt="" className="w-10 h-10 object-cover shrink-0 border border-black/8" />
                                            ) : (
                                                <div className="w-10 h-10 shrink-0 bg-black/5 flex items-center justify-center text-xl text-coal/20">
                                                    {MEDIA_TYPES.find((m) => m.key === mediaType)?.icon}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-bold text-coal truncate">{item.title}</div>
                                                {item.subtitle && <div className="text-[11px] text-coal/50 truncate">{item.subtitle}</div>}
                                                {item.badges?.[0] && <div className="text-[10px] text-coal/35">{item.badges[0]}</div>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {step === 3 && (
                    <div className="shrink-0 border-t border-black/10 px-4 py-3 flex justify-end gap-2 bg-black/2">
                        <button type="button" onClick={onClose} className="px-4 py-1.5 text-sm border border-black/20 text-coal hover:bg-black/6 transition-colors">Cancel</button>
                        <button
                            type="button"
                            disabled={saving || !selected}
                            onClick={handleSave}
                            className="px-4 py-1.5 text-sm bg-coal text-offwhite border border-black/40 hover:bg-gold hover:text-coal hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            {saving ? 'Saving…' : 'Save Status'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const CardRail = ({ cards, ownHabboName, onDelete, onLoadMore, hasMore, loadingMore, palette }) => {
    const railRef = useRef(null);
    const touchRef = useRef(null);

    const handleTouchStart = (e) => {
        touchRef.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        if (touchRef.current === null) return;
        const dx = touchRef.current - e.touches[0].clientX;
        if (railRef.current) railRef.current.scrollLeft += dx;
        touchRef.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        touchRef.current = null;
    };

    const dividerIdx = cards.some(c => c.isOwn)
        ? cards.findIndex((c, i) => i > 0 && !c.isOwn)
        : -1;

    return (
        <>
            <style>{`.habbo-scroll-h::-webkit-scrollbar{width:16px;height:16px}.habbo-scroll-h::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll-h::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll-h::-webkit-scrollbar-thumb:hover{background:#7a7a7a}.habbo-scroll-v::-webkit-scrollbar{width:16px}.habbo-scroll-v::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll-v::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll-v::-webkit-scrollbar-thumb:hover{background:#7a7a7a}`}</style>
            <div
                ref={railRef}
                className="habbo-scroll-h flex gap-3 overflow-x-auto px-3 py-3"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ scrollbarWidth: 'auto', WebkitOverflowScrolling: 'touch' }}
            >
                {cards.map((card, i) => (
                    <React.Fragment key={cardKey(card, i)}>
                        {i === dividerIdx && dividerIdx > 0 && (
                            <div className="shrink-0 flex flex-col items-center gap-1 px-0.5 h-[330px]">
                                <div className="flex-1 w-px bg-black/15" />
                                <span className="text-[9px] text-coal/25 font-bold uppercase tracking-[0.15em] [writing-mode:vertical-rl]">Friends</span>
                                <div className="flex-1 w-px bg-black/15" />
                            </div>
                        )}
                        <StatusCard
                            card={card}
                            isOwnCard={card.isOwn && card.habboName === ownHabboName}
                            onDelete={onDelete}
                            palette={palette}
                        />
                    </React.Fragment>
                ))}

                {hasMore && (
                    <div className="shrink-0 flex flex-col items-center justify-center gap-2 border border-black/10 bg-white/60 w-[100px] h-[330px]">
                        <button
                            type="button"
                            onClick={onLoadMore}
                            disabled={loadingMore}
                            className="px-2 py-1.5 text-xs border border-black/20 text-coal/50 hover:bg-black/6 transition-colors disabled:opacity-40"
                        >
                            {loadingMore ? 'Loading…' : 'Load more'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

const StatusBox = ({
                       profileUsername,
                       isOwnProfile,
                       isLoggedIn,
                       currentUsername,
                       isGuest,
                       headerColour,
                       initialStatus,
                       initialStatusHistory,
                       initialCommunityStatuses = [],
                   }) => {
    const palette = useMemo(() => buildStatusPalette(headerColour), [headerColour]);

    const [ownStatus, setOwnStatus] = useState(null);
    const [ownStatusLoaded, setOwnStatusLoaded] = useState(false);

    const [friends, setFriends] = useState([]);
    const [friendsOffset, setFriendsOffset] = useState(0);
    const [friendsHasMore, setFriendsHasMore] = useState(false);
    const [friendsLoading, setFriendsLoading] = useState(false);

    const [history, setHistory] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [historyShown, setHistoryShown] = useState(HISTORY_BATCH);

    const [community, setCommunity] = useState(
        Array.isArray(initialCommunityStatuses)
            ? initialCommunityStatuses.map(normaliseIncomingStatus).filter(Boolean)
            : []
    );
    const [communityLoaded, setCommunityLoaded] = useState(Array.isArray(initialCommunityStatuses));
    const [communityLoading, setCommunityLoading] = useState(false);

    const [activeView, setActiveView] = useState('friends');
    const [showModal, setShowModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [refreshCooldown, setRefreshCooldown] = useState(0);

    const cooldownRef = useRef(null);
    const communityIntervalRef = useRef(null);
    const communityLoadedRef = useRef(communityLoaded);
    useEffect(() => { communityLoadedRef.current = communityLoaded; }, [communityLoaded]);

    const ownStatusMigratedRef = useRef(false);
    const historyMigratedRef = useRef(false);

    const showToggle = isOwnProfile && isLoggedIn && !isGuest;

    useEffect(() => {
        setOwnStatus(null);
        setOwnStatusLoaded(false);
        setHistory([]);
        setHistoryLoaded(false);
        setHistoryShown(HISTORY_BATCH);
        setFriends([]);
        setFriendsOffset(0);
        setFriendsHasMore(false);
        setCommunity([]);
        setCommunityLoaded(false);
        setActiveView('friends');
        ownStatusMigratedRef.current = false;
        historyMigratedRef.current = false;
    }, [profileUsername]);

    useEffect(() => {
        if (!Array.isArray(initialCommunityStatuses) || initialCommunityStatuses.length === 0) return;
        setCommunity(initialCommunityStatuses.map(normaliseIncomingStatus).filter(Boolean));
        setCommunityLoaded(true);
    }, [initialCommunityStatuses]);

    useEffect(() => {
        if (!profileUsername) return;
        if (initialStatus !== undefined) {
            if (initialStatus) setOwnStatus(initialStatus);
            setOwnStatusLoaded(true);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const res = await getProfileData(profileUsername, 'status');
                if (cancelled) return;
                if (res?.success && res.data?.jsonPayload) {
                    const p = typeof res.data.jsonPayload === 'string' ? JSON.parse(res.data.jsonPayload) : res.data.jsonPayload;
                    setOwnStatus(p);
                }
            } catch {
            } finally {
                if (!cancelled) setOwnStatusLoaded(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [profileUsername, initialStatus]);

    useEffect(() => {
        if (isOwnProfile || !profileUsername) return;
        if (Array.isArray(initialStatusHistory) && initialStatusHistory.length > 0) {
            setHistory(initialStatusHistory);
            setHistoryLoaded(true);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const res = await getStatusHistoryItems(profileUsername);
                if (cancelled) return;
                if (res?.success && Array.isArray(res.items)) {
                    setHistory(res.items);
                }
            } catch {
            } finally {
                if (!cancelled) setHistoryLoaded(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [profileUsername, isOwnProfile, initialStatusHistory]);

    useEffect(() => {
        if (!isOwnProfile || activeView !== 'history' || historyLoaded) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await getStatusHistoryItems(profileUsername);
                if (cancelled) return;
                if (res?.success && Array.isArray(res.items)) {
                    setHistory(res.items);
                }
            } catch {
            } finally {
                if (!cancelled) setHistoryLoaded(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [activeView, historyLoaded, profileUsername, isOwnProfile]);

    const loadFriends = useCallback(async (offset) => {
        if (!isLoggedIn || isGuest) return;
        setFriendsLoading(true);
        try {
            const res = await getFriendsStatuses(FRIENDS_BATCH, offset);
            if (!res?.success) return;
            const incoming = (res.statuses || [])
                .map(normaliseIncomingStatus)
                .filter(Boolean)
                .map((f) => ({ ...f, isOwn: false }));
            setFriends((prev) => (offset === 0 ? incoming : [...prev, ...incoming]));
            setFriendsOffset(offset + incoming.length);
            setFriendsHasMore(!!res.hasMore);
        } catch {
        } finally {
            setFriendsLoading(false);
        }
    }, [isLoggedIn, isGuest]);

    const loadCommunity = useCallback(async ({ silent = false } = {}) => {
        if (!isLoggedIn || isGuest) return;
        if (!silent) setCommunityLoading(true);
        try {
            const res = await getCommunityStatuses(COMMUNITY_LIMIT, 0);
            if (!res?.success) return;
            const incoming = (res.statuses || [])
                .map(normaliseIncomingStatus)
                .filter(Boolean)
                .slice(0, COMMUNITY_LIMIT);
            setCommunity(incoming);
            setCommunityLoaded(true);
        } catch {
        } finally {
            if (!silent) setCommunityLoading(false);
        }
    }, [isLoggedIn, isGuest]);

    useEffect(() => {
        if (!isOwnProfile || !ownStatusLoaded || ownStatusMigratedRef.current) return;
        if (!ownStatus?.thumbnail || ownStatus.thumbnail.startsWith('data:')) return;
        ownStatusMigratedRef.current = true;
        let cancelled = false;
        (async () => {
            try {
                const result = await updateCurrentStatusThumbnail(ownStatus);
                if (cancelled || !result?.success) return;
                // Re-load the status from the server so we display the processed blob
                const fresh = await getProfileData(profileUsername, 'status');
                if (!cancelled && fresh?.data?.jsonPayload) {
                    const p = typeof fresh.data.jsonPayload === 'string' ? JSON.parse(fresh.data.jsonPayload) : fresh.data.jsonPayload;
                    setOwnStatus(p);
                }
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [isOwnProfile, ownStatusLoaded, ownStatus]);

    useEffect(() => {
        if (!isOwnProfile || !historyLoaded || historyMigratedRef.current) return;
        const toMigrate = history.filter(h => h.thumbnail && !h.thumbnail.startsWith('data:') && h.rowId);
        if (!toMigrate.length) return;
        historyMigratedRef.current = true;
        let cancelled = false;
        (async () => {
            await Promise.allSettled(toMigrate.map(e => updateStatusHistoryItemRow(e.rowId, e)));
            if (cancelled) return;
            // Reload history so display shows the processed blobs
            const fresh = await getStatusHistoryItems(profileUsername);
            if (!cancelled && fresh?.items) setHistory(fresh.items);
        })();
        return () => { cancelled = true; };
    }, [isOwnProfile, historyLoaded, history]);

    const startCooldown = useCallback((secs = 30) => {
        setRefreshCooldown(secs);
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        cooldownRef.current = setInterval(() => {
            setRefreshCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    useEffect(() => {
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            if (communityIntervalRef.current) clearInterval(communityIntervalRef.current);
        };
    }, []);

    const handleRefresh = useCallback(async () => {
        if (refreshCooldown > 0) return;

        startCooldown(30);

        setOwnStatusLoaded(false);
        setOwnStatus(null);

        try {
            const res = await getProfileData(profileUsername, 'status');
            if (res?.success && res.data?.jsonPayload) {
                const p = typeof res.data.jsonPayload === 'string' ? JSON.parse(res.data.jsonPayload) : res.data.jsonPayload;
                setOwnStatus(p);
            }
        } catch {
        } finally {
            setOwnStatusLoaded(true);
        }

        if (isOwnProfile) {
            if (activeView === 'friends') {
                setFriends([]);
                setFriendsOffset(0);
                setFriendsHasMore(false);
                await loadFriends(0);
            } else if (activeView === 'community') {
                await loadCommunity();
            } else {
                setHistoryLoaded(false);
                setHistoryShown(HISTORY_BATCH);
                setHistory([]);
            }
        } else {
            setHistoryLoaded(false);
            setHistoryShown(HISTORY_BATCH);
            setHistory([]);
        }
    }, [refreshCooldown, startCooldown, profileUsername, isOwnProfile, activeView, loadFriends, loadCommunity]);

    useEffect(() => {
        if (!isOwnProfile) return;
        loadFriends(0);
    }, [isOwnProfile, loadFriends]);

    useEffect(() => {
        if (!isOwnProfile) return;
        const handler = (e) => {
            const card = normaliseIncomingStatus(e.detail);
            if (!card?.habboName) return;
            const name = card.habboName.toLowerCase();
            setFriends(prev => {
                const idx = prev.findIndex(f => f.habboName?.toLowerCase() === name);
                if (idx === -1) return prev;
                const next = [...prev];
                next[idx] = { ...card, isOwn: false };
                return next;
            });
        };
        window.addEventListener('frankly:statusPosted', handler);
        return () => window.removeEventListener('frankly:statusPosted', handler);
    }, [isOwnProfile]);

    useEffect(() => {
        if (!isOwnProfile) return;
        if (activeView !== 'community') {
            if (communityIntervalRef.current) {
                clearInterval(communityIntervalRef.current);
                communityIntervalRef.current = null;
            }
            return;
        }

        loadCommunity({ silent: communityLoadedRef.current });

        if (communityIntervalRef.current) clearInterval(communityIntervalRef.current);
        communityIntervalRef.current = setInterval(() => {
            loadCommunity({ silent: true });
        }, COMMUNITY_AUTO_REFRESH_MS);

        return () => {
            if (communityIntervalRef.current) {
                clearInterval(communityIntervalRef.current);
                communityIntervalRef.current = null;
            }
        };
    }, [activeView, isOwnProfile, loadCommunity]);

    const handleSave = useCallback(async (payload) => {
        if (ownStatus?.timestamp) {
            const elapsed = Date.now() - new Date(ownStatus.timestamp).getTime();
            if (elapsed < STATUS_COOLDOWN_MS) {
                const remaining = Math.ceil((STATUS_COOLDOWN_MS - elapsed) / 60000);
                toast.error(`You can update your status once every 30 minutes. Try again in ${remaining} minute${remaining !== 1 ? 's' : ''}.`);
                return;
            }
        }

        const result = await upsertOwnStatusWithHistory(payload, payload);

        ownStatusMigratedRef.current = false;
        setOwnStatus(payload);
        setHistory(prev => [{ ...payload, rowId: result?.rowId }, ...prev].slice(0, HISTORY_MAX));
        setShowModal(false);
        toast.success('Status updated!');
    }, [ownStatus]);

    const handleDelete = useCallback(async () => {
        if (deleting) return;
        setDeleting(true);

        try {
            let current = history;
            if (!historyLoaded) {
                try {
                    const res = await getStatusHistoryItems(profileUsername);
                    if (res?.success && Array.isArray(res.items)) {
                        current = res.items;
                        setHistory(current);
                        setHistoryLoaded(true);
                    }
                } catch {}
            }

            const [mostRecent, ...olderItems] = current;

            if (mostRecent?.rowId) {
                await deleteStatusHistoryItemRow(mostRecent.rowId);
            }

            if (olderItems.length > 0) {
                const prevStatus = olderItems[0];
                await upsertOwnProfileData('status', prevStatus);
                setOwnStatus(prevStatus);
                setHistory(olderItems);
                toast.success('Reverted to previous status.');
            } else {
                await deleteOwnProfileData('status');
                setOwnStatus(null);
                setHistory([]);
                toast.success('Status removed.');
            }
        } catch {
            toast.error('Could not remove status.');
        } finally {
            setDeleting(false);
        }
    }, [deleting, history, historyLoaded, profileUsername]);

    const friendsCards = useMemo(() => {
        if (!isOwnProfile) return [];
        const ownCard = ownStatus
            ? { ...ownStatus, habboName: profileUsername, isOwn: true }
            : { noStatus: true, habboName: profileUsername, isOwn: true };
        const sorted = [...friends].sort((a, b) => {
            const ta = new Date(a.updatedAt || a.timestamp || 0).getTime();
            const tb = new Date(b.updatedAt || b.timestamp || 0).getTime();
            return tb - ta;
        });
        return [ownCard, ...sorted];
    }, [isOwnProfile, ownStatus, friends, profileUsername]);

    const ownHistoryCards = useMemo(() => {
        return history.slice(0, historyShown).map((entry, i) => ({
            ...entry,
            habboName: profileUsername,
            isOwn: true,
            _historyIndex: i,
        }));
    }, [history, historyShown, profileUsername]);

    const otherProfileCards = useMemo(() => {
        if (isOwnProfile) return [];
        if (history.length > 0) {
            return history.slice(0, historyShown).map((entry, i) => ({
                ...entry,
                habboName: profileUsername,
                isOwn: false,
                _historyIndex: i,
            }));
        }
        return [
            ownStatus
                ? { ...ownStatus, habboName: profileUsername, isOwn: false }
                : { noStatus: true, habboName: profileUsername, isOwn: false },
        ];
    }, [isOwnProfile, ownStatus, history, historyShown, profileUsername]);

    if (!ownStatusLoaded) {
        return <div className="px-3 py-8 text-coal/40 text-sm text-center">Loading…</div>;
    }

    const cards = isOwnProfile
        ? activeView === 'history'
            ? ownHistoryCards
            : friendsCards
        : otherProfileCards;

    const hasMore = isOwnProfile
        ? activeView === 'friends'
            ? friendsHasMore
            : activeView === 'history'
                ? historyShown < history.length
                : false
        : history.length > 0 && historyShown < history.length;

    return (
        <div className="flex flex-col" style={{ background: palette.containerBg }}>
            {showToggle && (
            <div className="flex items-center border-b border-black/20 bg-black/5">
                <button
                    type="button"
                    onClick={() => setActiveView('friends')}
                    className={`px-3 py-1.5 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${activeView === 'friends' ? 'border-coal text-coal' : 'border-transparent text-coal/50 hover:text-coal/80'}`}
                >
                    Friends
                </button>
                <button
                    type="button"
                    onClick={() => setActiveView('community')}
                    className={`px-3 py-1.5 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${activeView === 'community' ? 'border-coal text-coal' : 'border-transparent text-coal/50 hover:text-coal/80'}`}
                >
                    FranklySphere
                </button>
                <button
                    type="button"
                    onClick={() => setActiveView('history')}
                    className={`px-3 py-1.5 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${activeView === 'history' ? 'border-coal text-coal' : 'border-transparent text-coal/50 hover:text-coal/80'}`}
                >
                    My History
                </button>
                <div className="ml-auto flex items-stretch border-l border-black/20">
                    <button
                        type="button"
                        onClick={() => setShowModal(true)}
                        className="px-2 py-1.5 text-xs font-bold text-coal/50 hover:text-coal transition-colors whitespace-nowrap"
                    >
                        + Update Status
                    </button>
                </div>
            </div>
        )}

            {isOwnProfile && activeView === 'community' ? (
                <CommunitySphere cards={community} palette={palette} loading={communityLoading} />
            ) : cards.length === 0 ? (
                <div className="flex flex-col items-center gap-1.5 py-8 text-center">
                    <p className="text-coal/40 text-sm">
                        {!isOwnProfile
                            ? (historyLoaded ? 'No statuses yet.' : 'Loading…')
                            : activeView === 'history'
                                ? (historyLoaded ? 'No history yet.' : 'Loading…')
                                : 'No statuses yet.'}
                    </p>
                </div>
            ) : (
                <CardRail
                    cards={cards}
                    ownHabboName={profileUsername}
                    onDelete={handleDelete}
                    hasMore={hasMore}
                    loadingMore={friendsLoading}
                    palette={palette}
                    onLoadMore={() => {
                        if (isOwnProfile && activeView === 'friends') loadFriends(friendsOffset);
                        else setHistoryShown((n) => n + HISTORY_BATCH);
                    }}
                />
            )}

            {activeView !== 'community' && <div className="h-2" />}

            {showModal && <StatusCreationModal onSave={handleSave} onClose={() => setShowModal(false)} />}
        </div>
    );
};

export default StatusBox;
